/* eslint-disable prettier/prettier */
import { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { FileUploadZone } from '../ui/FileUploadZone';
import { Label } from '../ui/Label';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { FileService } from '../../services/file.service';
import { FolderService } from '../../services/folder.service';
import { FolderType } from '../../types/FolderType';
import { CustomSelect } from '../ui/Select';

interface FileUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  forcedFolderId?: string;
}

const MSG_NOT_TREATY = 'This document is not a valid reinsurance treaty.';
const MSG_AI_DOWN = 'The AI module is down. Please try again later.';

export const FileUploadModal = ({
  isOpen,
  onClose,
  onSuccess,
  forcedFolderId,
}: FileUploadModalProps) => {
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'error' | 'success'>('idle');
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState<string | undefined>(forcedFolderId);
  const [folders, setFolders] = useState<FolderType[]>([]);
  const [noFileError, setNoFileError] = useState(false);
  const [typeError, setTypeError] = useState(false);
  const [multiFileError, setMultiFileError] = useState(false);
  const [dots, setDots] = useState('');
  const [duplicateNameError, setDuplicateNameError] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string>('');
  const [isRetry, setIsRetry] = useState(false);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (uploadStatus === 'uploading') {
      interval = setInterval(() => {
        setDots((prev) => (prev.length < 3 ? prev + '.' : ''));
      }, 500);
    } else {
      setDots('');
    }
    return () => clearInterval(interval);
  }, [uploadStatus]);

  useEffect(() => {
    if (!isOpen) return;

    setSelectedFolderId(forcedFolderId);

    const fetchFolders = async () => {
      try {
        const data = await FolderService.getAllWithHierarchy();
        setFolders(data);
      } catch (err) {
        console.error('Failed to load folders', err);
      }
    };

    fetchFolders();
  }, [isOpen, forcedFolderId]);

  const flattenFolders = (list: FolderType[], prefix = ''): { label: string; value: string }[] =>
    list.flatMap((folder) => {
      const label = prefix ? `${prefix} > ${folder.folderName}` : folder.folderName;
      const children = flattenFolders(folder.subfolders || [], label);
      return [{ label, value: folder.id }, ...children];
    });

  const reset = () => {
    setUploadStatus('idle');
    setFile(null);
    setTitle('');
    setDescription('');
    setSelectedFolderId(undefined);
    setNoFileError(false);
    setTypeError(false);
    setMultiFileError(false);
    setDuplicateNameError(false);
    setUploadErrorMessage('');
    setIsRetry(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleRemoveFile = () => {
    setFile(null);
    setNoFileError(false);
    setTypeError(false);
    setMultiFileError(false);
    setDuplicateNameError(false);
    setUploadErrorMessage('');
    setUploadStatus('idle');
    setIsRetry(false);
  };

  const handleUpload = async () => {
    setNoFileError(false);
    setTypeError(false);
    setMultiFileError(false);
    setDuplicateNameError(false);
    setUploadErrorMessage('');

    if (!file) {
      setNoFileError(true);
      return;
    }

    try {
      if (!isRetry) {
        const isDuplicate = await FileService.checkDuplicateFileName(file.name);
        if (isDuplicate) {
          setDuplicateNameError(true);
          return;
        }
      }

      setUploadStatus('uploading');

      if (isRetry) {
        const isDuplicate = await FileService.checkDuplicateFileName(file.name);
        if (isDuplicate) {
          setUploadStatus('error');
          setDuplicateNameError(true);
          if (!uploadErrorMessage) setUploadErrorMessage('File name already exists. Please modify the name.');
          return;
        }
      }

      await FileService.uploadFile(file, title, description, selectedFolderId);

      setUploadStatus('success');
      onSuccess?.();
    } catch (e: any) {
      setUploadStatus('error');
      let message = 'An unexpected error occurred. Please try again later.';
      if (e?.response?.data?.message) {
        message = typeof e.response.data.message === 'string'
          ? e.response.data.message
          : (e.response.data.message?.message ?? message);
      } else if (e?.message) {
        message = e.message;
      }
      setUploadErrorMessage(message);

      if (message === MSG_NOT_TREATY) {
        setIsRetry(false);  
      } else if (message === MSG_AI_DOWN) {
        setIsRetry(true);  
      } else {
        setIsRetry(true);
      }
    }
  };

  const handleTryAgain = () => {
    if (uploadErrorMessage === MSG_NOT_TREATY) {
      const keepFolder = selectedFolderId;
      reset();
      setSelectedFolderId(keepFolder);
    } else if (uploadErrorMessage === MSG_AI_DOWN) {
      setIsRetry(true);
      handleUpload();
    } else {
      setIsRetry(true);
      handleUpload();
    }
  };

  const folderOptions = flattenFolders(folders.filter((f) => f.id !== 'uncategorized'));

  const getTitle = () => {
    if (uploadStatus === 'uploading') return `Uploading${dots}`;
    if (uploadStatus === 'error') {
      return uploadErrorMessage === MSG_NOT_TREATY
        ? 'Upload Failed — Try Again'
        : 'Upload Failed — Try Again';
    }
    if (uploadStatus === 'success') return 'Upload Complete 👌!';
    return 'Upload a New File';
  };

  const hasAnyError =
    uploadStatus === 'error' || noFileError || typeError || multiFileError || duplicateNameError;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={getTitle()}
      disableOutsideClick={uploadStatus === 'uploading'}
      hideCloseButton={uploadStatus === 'uploading'}
    >
      <FileUploadZone
        file={file}
        onFileSelect={setFile}
        noFileError={noFileError}
        setNoFileError={setNoFileError}
        setTypeError={setTypeError}
        setMultiFileError={setMultiFileError}
        typeError={typeError}
        multiFileError={multiFileError}
        uploadStatus={uploadStatus}
        duplicateNameError={duplicateNameError}
        setDuplicateNameError={setDuplicateNameError}
        uploadErrorMessage={uploadErrorMessage}
        onRemoveFile={handleRemoveFile}
       
      />

      {uploadStatus === 'idle' && (
        <>
          <Label className="mt-5 mb-1 text-lg font-semibold">Title</Label>
          <Input
            placeholder="Enter a title (optional)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          <Label className="mt-4 mb-1 text-lg font-semibold">Description</Label>
          <Input
            placeholder="Enter a description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <Label className="mt-4 mb-1 text-lg font-semibold">Folder</Label>
          <CustomSelect
            placeholder="Select a folder (optional)"
            data={[{ label: 'None', value: '' }, ...folderOptions]}
            value={selectedFolderId ?? ''}
            onChange={(val) => setSelectedFolderId(val || undefined)}
            disabled={!!forcedFolderId}
          />
        </>
      )}

      {uploadStatus !== 'uploading' && uploadStatus !== 'success' && (
        <div className="flex justify-center gap-2 mt-5 mb-3 sm:justify-end">
          <Button variant="secondary" onClick={handleClose} className="px-7 sm:px-10">
            Cancel
          </Button>
          <Button className="px-6 sm:px-7" onClick={uploadStatus === 'error' ? handleTryAgain : handleUpload}>
            {uploadStatus === 'error'
              ? (uploadErrorMessage === MSG_NOT_TREATY ? 'Try Again' : 'Try Again')
              : 'Upload'}
          </Button>
        </div>
      )}
    </Modal>
  );
};
